/*
 * Mix flavour selector (rendered by snippets/mix-flavour-selector.liquid).
 *
 * Shows when the selected variant is a Mix variant. The customer allocates
 * bags across flavours. It writes NO form inputs - product-form below asks it
 * for the real single-bag flavour variants and adds those to the cart instead
 * of the Mix variant, so the order only carries real flavour SKUs.
 */
if (!customElements.get('mix-flavour-selector')) {
  customElements.define(
    'mix-flavour-selector',
    class MixFlavourSelector extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.formId = this.dataset.formId || '';
        this.unitLabel = this.dataset.unitLabel || '1kg';
        this.rows = Array.from(this.querySelectorAll('[data-mix-row]'));
        this.counterEl = this.querySelector('[data-mix-counter]');
        this.helpEl = this.querySelector('[data-mix-help]');
        this.summaryEl = this.querySelector('[data-mix-summary]');
        this.clearEl = this.querySelector('[data-mix-clear]');
        this.errorEl = this.querySelector('[data-mix-error]');
        this.bags = 0;

        try {
          const json = this.querySelector('[data-mix-variants]');
          this.mixVariants = json ? JSON.parse(json.textContent) : {};
        } catch (e) {
          this.mixVariants = {};
        }

        this.onLocalClick = this.handleLocalClick.bind(this);
        this.addEventListener('click', this.onLocalClick);

        this.onDocumentChange = (event) => {
          const target = event.target;
          if (!target || this.contains(target)) return;
          if (target.name === 'id' || target.name === 'quantity') this.refresh();
        };
        document.addEventListener('change', this.onDocumentChange);

        this.onVariantChange = () => this.refresh();
        document.addEventListener('variant:change', this.onVariantChange);

        this.refresh();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onLocalClick);
        document.removeEventListener('change', this.onDocumentChange);
        document.removeEventListener('variant:change', this.onVariantChange);
        this.initialised = false;
      }

      get form() {
        return this.formId ? document.getElementById(this.formId) : null;
      }

      get scope() {
        return this.closest('product-info') || document;
      }

      currentVariantId() {
        const form = this.form;
        const input = form ? form.querySelector('input[name="id"]') : null;
        return input ? String(input.value) : '';
      }

      formQuantity() {
        const form = this.form;
        if (!form) return 1;
        const value = parseInt(new FormData(form).get('quantity'), 10);
        return value > 0 ? value : 1;
      }

      isMixVariant(variantId) {
        return Object.prototype.hasOwnProperty.call(this.mixVariants, String(variantId));
      }

      isActive() {
        return !this.hidden && this.bags > 0;
      }

      allocated() {
        return this.rows.reduce((total, row) => total + (parseInt(row.dataset.count, 10) || 0), 0);
      }

      remaining() {
        const left = this.bags - this.allocated();
        return left > 0 ? left : 0;
      }

      isComplete() {
        return this.bags > 0 && this.allocated() === this.bags;
      }

      incompleteMessage() {
        return 'Choose a flavour for every bag before adding to cart (' + this.allocated() + ' of ' + this.bags + ' chosen).';
      }

      flagIncomplete() {
        if (this.errorEl) this.errorEl.hidden = false;
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Real single-bag flavour variants, multiplied by the form quantity.
      getItems(multiplier, sellingPlan) {
        const times = multiplier > 0 ? multiplier : 1;
        const items = [];
        this.rows.forEach((row) => {
          const count = parseInt(row.dataset.count, 10) || 0;
          const id = parseInt(row.dataset.variantId, 10);
          if (count <= 0 || !id) return;
          const item = { id: id, quantity: count * times };
          if (sellingPlan) item.selling_plan = sellingPlan;
          items.push(item);
        });
        return items;
      }

      handleLocalClick(event) {
        const step = event.target.closest('[data-mix-step]');
        const fill = event.target.closest('[data-mix-fill]');
        const clear = event.target.closest('[data-mix-clear]');
        if (!step && !fill && !clear) return;

        event.preventDefault();

        if (clear) {
          this.rows.forEach((row) => (row.dataset.count = '0'));
          this.render();
          return;
        }

        const row = event.target.closest('[data-mix-row]');
        if (!row || row.dataset.available === 'false') return;

        const current = parseInt(row.dataset.count, 10) || 0;

        if (fill) {
          row.dataset.count = String(current + this.remaining());
          this.render();
          return;
        }

        const delta = parseInt(step.getAttribute('data-mix-step'), 10) || 0;
        if (delta > 0 && this.remaining() <= 0) return;
        row.dataset.count = String(Math.max(0, current + delta));
        this.render();
      }

      togglePaymentButtons(hide) {
        this.scope.querySelectorAll('.shopify-payment-button').forEach((el) => {
          el.style.display = hide ? 'none' : '';
        });
      }

      refresh() {
        const variantId = this.currentVariantId();
        const bags = this.isMixVariant(variantId) ? parseInt(this.mixVariants[variantId], 10) || 0 : 0;

        if (bags < 2) {
          this.hidden = true;
          this.bags = 0;
          this.rows.forEach((row) => (row.dataset.count = '0'));
          if (this.errorEl) this.errorEl.hidden = true;
          this.togglePaymentButtons(false);
          return;
        }

        this.hidden = false;
        // Buy-it-now would check out the Mix variant itself, so it is hidden
        // while a Mix is selected. Add to Cart carries the real SKUs.
        this.togglePaymentButtons(true);

        if (bags !== this.bags) {
          this.bags = bags;
          let over = this.allocated() - bags;
          for (let i = this.rows.length - 1; i >= 0 && over > 0; i--) {
            const count = parseInt(this.rows[i].dataset.count, 10) || 0;
            const take = Math.min(count, over);
            this.rows[i].dataset.count = String(count - take);
            over -= take;
          }
        }

        if (this.helpEl) {
          this.helpEl.textContent = 'Each bag is ' + this.unitLabel + '. Pick a flavour for all ' + bags + ' bags.';
        }

        this.render();
      }

      formatMoney(cents) {
        try {
          if (window.theme && theme.Currency && theme.shopSettings) {
            return theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat || theme.shopSettings.moneyWithCurrencyFormat);
          }
        } catch (e) {}
        return '$' + (cents / 100).toFixed(2);
      }

      render() {
        const remaining = this.remaining();
        const allocated = this.allocated();
        const complete = this.isComplete();

        this.rows.forEach((row) => {
          const count = parseInt(row.dataset.count, 10) || 0;
          const soldOut = row.dataset.available === 'false';
          const qty = row.querySelector('[data-mix-qty]');
          const minus = row.querySelector('[data-mix-step="-1"]');
          const plus = row.querySelector('[data-mix-step="1"]');
          const fill = row.querySelector('[data-mix-fill]');
          if (qty) qty.textContent = String(count);
          if (minus) minus.disabled = count <= 0;
          if (plus) plus.disabled = soldOut || remaining <= 0;
          if (fill) fill.disabled = soldOut || remaining <= 0;
        });

        if (this.counterEl) {
          this.counterEl.textContent = allocated + ' of ' + this.bags + ' bags chosen';
          this.counterEl.toggleAttribute('data-mix-done', complete);
        }

        if (this.clearEl) this.clearEl.hidden = allocated <= 0;
        if (complete && this.errorEl) this.errorEl.hidden = true;

        if (this.summaryEl) {
          const times = this.formQuantity();
          const parts = [];
          let total = 0;
          this.rows.forEach((row) => {
            const count = (parseInt(row.dataset.count, 10) || 0) * times;
            if (count <= 0) return;
            parts.push(count + ' x ' + row.dataset.flavour);
            total += count * (parseInt(row.dataset.price, 10) || 0);
          });
          if (complete && parts.length) {
            this.summaryEl.textContent =
              'Adds ' + parts.join(', ') + ' (' + this.unitLabel + ' bags): ' + this.formatMoney(total) +
              ' before multibuy savings, which apply automatically in your cart.';
            this.summaryEl.hidden = false;
          } else {
            this.summaryEl.textContent = '';
            this.summaryEl.hidden = true;
          }
        }
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        //this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.cart = document.querySelector('mini-cart');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.blockSubmitIfRequiredFieldsEmpty(evt)) return;

        this.handleErrorMessage();

        let shakerItems = [];
        try {
          shakerItems = this.getShakerItems();
        } catch (e) {
          console.error(e);
        }

        // Mix is read straight from the page (data attributes), so it works
        // even if the selector's own script has not started.
        let mix = null;
        try {
          mix = this.getMixState();
        } catch (e) {
          console.error(e);
          this.handleErrorMessage('Your flavour choices could not be read (' + (e && e.message ? e.message : e) + '). Please refresh the page and try again.');
          return;
        }

        if (mix) {
          if (!mix.complete) {
            this.flagMixIncomplete(mix);
            return;
          }
          const mixFormData = new FormData(this.form);
          const quantity = parseInt(mixFormData.get('quantity'), 10) || 1;
          const sellingPlan = mixFormData.get('selling_plan') || '';
          const items = this.buildMixItems(mix, quantity, sellingPlan).concat(shakerItems);
          if (!items.length) {
            this.flagMixIncomplete(mix);
            return;
          }
          this.addItems(items);
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        //this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${theme.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // Shakers ticked under the subscription widget go in after the
            // product itself, so a failed product add never leaves a lone shaker.
            if (shakerItems.length) {
              return this.postItems(shakerItems).then((shakerResponse) => {
                if (shakerResponse.status) {
                  this.handleErrorMessage(shakerResponse.description);
                  this.finishAdd(response, formData.get('id'));
                  return;
                }
                this.finishAdd(shakerResponse, formData.get('id'));
              });
            }

            this.finishAdd(response, formData.get('id'));
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('Could not add to cart (' + (e && e.message ? e.message : e) + '). Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            //this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // Adds several real variants in one request (Mix flavours + shakers).
      addItems(items) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (this.cart) this.cart.setActiveElement(document.activeElement);

        this.postItems(items)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: items[0].id,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message);
              return;
            }
            this.finishAdd(response, items[0].id);
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('Could not add to cart (' + (e && e.message ? e.message : e) + '). Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.submitButton.removeAttribute('aria-disabled');
          });
      }

      postItems(items) {
        const payload = { items: items };
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }
        return fetch(`${theme.routes.cart_add_url}`, {
          ...fetchConfig('javascript'),
          body: JSON.stringify(payload),
        })
          .then((response) => response.text().then((text) => ({ status: response.status, text: text })))
          .then(({ status, text }) => {
            try {
              return JSON.parse(text);
            } catch (e) {
              throw new Error('unexpected cart response, status ' + status);
            }
          });
      }

      finishAdd(response, productVariantId) {
        if (!this.cart) {
          window.location = theme.routes.cart_url;
          return;
        }

        if (!this.error) {
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: productVariantId,
              cart: response,
            });
          } catch (e) {
            console.error(e);
          }
        }
        this.error = false;

        // The items are already in the cart at this point. If the drawer
        // cannot redraw, open the cart page so the customer still sees them.
        const render = () => {
          try {
            this.cart.renderContents(response);
          } catch (e) {
            console.error(e);
            window.location = theme.routes.cart_url;
          }
        };

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(render);
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          render();
        }
      }

      // Reads the Mix selection from the page. Returns null when the selected
      // variant is not a Mix.
      getMixState() {
        const selector = this.getMixSelector();
        if (!selector) return null;

        if (window.customElements && typeof customElements.upgrade === 'function') {
          customElements.upgrade(selector);
        }

        const json = selector.querySelector('[data-mix-variants]');
        const map = json ? JSON.parse(json.textContent) : {};
        const idInput = this.form.querySelector('input[name="id"]');
        const variantId = idInput ? String(idInput.value) : '';
        const bags = Object.prototype.hasOwnProperty.call(map, variantId) ? parseInt(map[variantId], 10) || 0 : 0;
        if (bags < 2) return null;

        const rows = Array.from(selector.querySelectorAll('[data-mix-row]'));
        const allocated = rows.reduce((total, row) => total + (parseInt(row.dataset.count, 10) || 0), 0);
        return { selector: selector, rows: rows, bags: bags, allocated: allocated, complete: allocated === bags };
      }

      buildMixItems(mix, multiplier, sellingPlan) {
        const times = multiplier > 0 ? multiplier : 1;
        const items = [];
        mix.rows.forEach((row) => {
          const count = parseInt(row.dataset.count, 10) || 0;
          const id = parseInt(row.dataset.variantId, 10);
          if (count <= 0 || !id) return;
          const item = { id: id, quantity: count * times };
          if (sellingPlan) item.selling_plan = sellingPlan;
          items.push(item);
        });
        return items;
      }

      flagMixIncomplete(mix) {
        const message =
          'Choose a flavour for every bag before adding to cart (' + mix.allocated + ' of ' + mix.bags + ' chosen).';
        mix.selector.hidden = false;
        const error = mix.selector.querySelector('[data-mix-error]');
        if (error) error.hidden = false;
        if (typeof mix.selector.scrollIntoView === 'function') {
          mix.selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        this.handleErrorMessage(message);
      }

      getMixSelector() {
        // Read the attribute: on a product form, this.form.id returns the
        // hidden <input name="id">, not the form's id.
        const formId = this.form ? this.form.getAttribute('id') : '';
        if (!formId) return null;
        const baseId = formId.replace(/--alt$/, '');
        const scope = this.closest('product-info') || document;
        return (
          scope.querySelector(`mix-flavour-selector[data-form-id="${baseId}"]`) ||
          document.querySelector(`mix-flavour-selector[data-form-id="${baseId}"]`)
        );
      }

      // Ticked "Can we offer you a glass shaker?" boxes (snippets/shaker-upsell-pdp.liquid).
      getShakerItems() {
        const scope = this.closest('product-info') || document;
        return Array.from(scope.querySelectorAll('input[data-shaker-upsell-pdp]:checked'))
          .map((input) => parseInt(input.dataset.variantId, 10))
          .filter((id) => id > 0)
          .map((id) => ({ id: id, quantity: 1 }));
      }

      blockSubmitIfRequiredFieldsEmpty(event) {
        if (!(this.form instanceof HTMLFormElement)) {
          throw new Error("Expected a form element.");
        }

        const formId = this.form.getAttribute('id');
        const requiredFields = document.querySelectorAll('[required]');
        let allFilled = true;
        for (const field of requiredFields) {
          const belongsToForm = (field.getAttribute('form') === formId || field.closest('form')?.id === formId);
          if (!belongsToForm) continue;

          let isValid = true;
          switch (field.type) {
            case 'checkbox':
            case 'radio':
              const group = document.querySelectorAll(`input[name="${field.name}"][type="${field.type}"]`);
              const isChecked = Array.from(group).some(input => input.checked);
              isValid = isChecked;
              break;
            case 'select-one':
              isValid = field.value !== '';
              break;
            case 'textarea':
            case 'text':
            case 'email':
            case 'number':
            case 'url':
            default:
              isValid = field.value.trim() !== '';
              break;
          }
          if (!isValid) {
            allFilled = false;
            field.classList.add('invalid');
          } else {
            field.classList.remove('invalid');
          }
        }

        if (!allFilled) {
          event.preventDefault();
          this.handleErrorMessage(theme.accessibilityStrings.fillInAllLineItemPropertyRequiredFields);
          return false;
        }
        return true;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = theme.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
